import {
	placementIdToChannelIdMap,
	placementIdToCreativeName,
} from 'src/lib/utils';
import {
	IFailedCreative,
	IPublishedCreative,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import {
	SuccessStatus,
	ChannelDisplayInfo,
	ErrorDetail,
	ChannelLinksMap,
} from './types';

/**
 * Generates channel-specific management links
 */
export const getChannelLinks = (
	fbPageId?: string,
	igName?: string,
): ChannelLinksMap => ({
	facebook: {
		url: fbPageId
			? `https://www.facebook.com/profile.php?id=${fbPageId}`
			: 'https://www.facebook.com',
		text: 'Manage your published ads',
	},
	facebookPaid: {
		url: 'https://business.facebook.com/latest/ad_center',
		text: 'Manage your published ads',
	},
	instagram: {
		url: igName
			? `https://www.instagram.com/${igName}`
			: 'https://www.instagram.com',
		text: 'Manage your published ads',
	},
	instagramPaid: {
		url: 'https://business.facebook.com/latest/ad_center',
		text: 'Manage your published ads',
	},
	google: {
		url: 'https://ads.google.com/',
		text: 'Manage your published ads',
	},
	googleDisplay: {
		url: 'https://ads.google.com/',
		text: 'Manage your published ads',
	},
	googlePMAX: {
		url: 'https://ads.google.com/',
		text: 'Manage your published ads',
	},
});

/**
 * Maps a creative's placementId or channel to its corresponding channel ID
 */
export const getChannelIdFromCreative = (
	creative: IFailedCreative | IPublishedCreative,
): string | null => {
	// Check if the creative has a direct channel property
	if ('channel' in creative && creative.channel) {
		// MetaAds is a container - don't show it as a separate channel
		if (creative.channel === 'metaAds') {
			return null; // Will be handled separately by getMetaAdsChannels
		}
		return creative.channel;
	}

	// Otherwise, use placementId mapping
	if (!creative.placementId) return null;
	return placementIdToChannelIdMap[creative.placementId] || null;
};

/**
 * Gets channels that should inherit metaAds errors
 */
export const getMetaAdsTargetChannels = (): string[] => {
	return ['facebookPaid', 'instagramPaid'];
};

/**
 * Gets all unique channel IDs that have associated creatives
 */
export const getChannelsWithActivity = (
	publishedCreatives: IPublishedCreative[],
	failedCreatives: IFailedCreative[],
): Set<string> => {
	const channelSet = new Set<string>();

	[...publishedCreatives, ...failedCreatives].forEach((creative) => {
		const channelId = getChannelIdFromCreative(creative);
		if (channelId) {
			channelSet.add(channelId);
		}

		// Handle metaAds channel errors - distribute to target channels
		if ('channel' in creative && creative.channel === 'metaAds') {
			getMetaAdsTargetChannels().forEach((targetChannel) => {
				channelSet.add(targetChannel);
			});
		}
	});

	return channelSet;
};

/**
 * Determines the success status of a channel based on its creatives
 */
export const calculateChannelStatus = (
	channelId: string,
	publishedCreatives: IPublishedCreative[],
	failedCreatives: IFailedCreative[],
	creativePublishedErrorCode: string,
): SuccessStatus => {
	// Find all creatives for this channel
	const channelCreatives = [...publishedCreatives, ...failedCreatives].filter(
		(creative) => {
			const creativeChannelId = getChannelIdFromCreative(creative);

			// Check if this creative matches the current channel
			if (creativeChannelId === channelId) {
				return true;
			}

			// For Facebook/Instagram Paid channels, also include metaAds errors
			if (
				(channelId === 'facebookPaid' || channelId === 'instagramPaid') &&
				'channel' in creative &&
				creative.channel === 'metaAds'
			) {
				return true;
			}

			return false;
		},
	);

	if (channelCreatives.length === 0) return 'error';

	let successfulCount = 0;
	let partialCount = 0; // Creatives with CREATIVE_PUBLISHED error

	channelCreatives.forEach((creative) => {
		if ('errors' in creative) {
			// This is a failed creative
			const hasOnlyCreativePublishedErrors = creative.errors.every(
				(e) => e.errorCode === creativePublishedErrorCode,
			);

			if (hasOnlyCreativePublishedErrors) {
				// Treat CREATIVE_PUBLISHED as partial success (already published)
				partialCount++;
			}
			// If it has other errors, it's a real failure (count = 0)
		} else {
			// This is a published creative (success)
			successfulCount++;
		}
	});

	const total = channelCreatives.length;
	const totalSuccessful = successfulCount + partialCount;

	if (totalSuccessful === total) {
		// All creatives were successful (either published or already published)
		return partialCount > 0 ? 'partial' : 'success';
	}

	if (totalSuccessful > 0) {
		// Some succeeded, some failed
		return 'partial';
	}

	// Nothing succeeded
	return 'error';
};

/**
 * Gets all errors for a specific channel
 */
export const getChannelErrors = (
	channelId: string,
	failedCreatives: IFailedCreative[],
): ErrorDetail[] => {
	const errors = failedCreatives
		.filter((creative) => {
			const creativeChannelId = getChannelIdFromCreative(creative);

			// Check if this creative matches the current channel
			if (creativeChannelId === channelId) {
				return true;
			}

			// For Facebook/Instagram Paid channels, also include metaAds errors
			if (
				(channelId === 'facebookPaid' || channelId === 'instagramPaid') &&
				'channel' in creative &&
				creative.channel === 'metaAds'
			) {
				return true;
			}

			return false;
		})
		.flatMap((creative) => {
			const placementName = creative.placementId
				? placementIdToCreativeName[creative.placementId] || ''
				: '';
			return creative.errors.map((error) => ({
				title: placementName,
				message: error.message,
				errorCode: error.errorCode,
			}));
		});

	// Remove duplicates and handle titles for single error types
	const messageCounts = new Map<string, number>();
	errors.forEach((error) => {
		messageCounts.set(
			error.message,
			(messageCounts.get(error.message) || 0) + 1,
		);
	});

	const uniqueErrors = Array.from(
		new Map(errors.map((error) => [error.message, error])).values(),
	);

	// If all errors have the same message, remove titles to avoid redundancy
	if (messageCounts.size === 1) {
		uniqueErrors.forEach((error) => (error.title = ''));
	}

	return uniqueErrors;
};

/**
 * Checks if there are any 500-type errors in the failed creatives
 */
export const has500Error = (
	failedCreatives: IFailedCreative[],
	error500Message: string,
): boolean => {
	return failedCreatives.some((fc) =>
		fc.errors.some((e) => e.message === error500Message),
	);
};

/**
 * Generates the main campaign status message
 */
export const generateMainMessage = (
	publishedCreatives: IPublishedCreative[],
	failedCreatives: IFailedCreative[],
	is500Error: boolean,
	creativePublishedErrorCode: string,
): string => {
	// Check for 500 errors first
	if (is500Error) {
		return "We couldn't publish the campaign, please try again.";
	}

	const totalCreatives = publishedCreatives.length + failedCreatives.length;
	if (totalCreatives === 0) {
		return 'No creatives to publish';
	}

	// Count successful publications
	const successfulPublications = publishedCreatives.length;

	// Count "already published" creatives (CREATIVE_PUBLISHED errors)
	const alreadyPublishedCount = failedCreatives.filter((fc) =>
		fc.errors.every((e) => e.errorCode === creativePublishedErrorCode),
	).length;

	const totalSuccessful = successfulPublications + alreadyPublishedCount;

	if (totalSuccessful === totalCreatives) {
		// Everything was successful (either newly published or already published)
		return 'Your campaign was published';
	}

	if (totalSuccessful > 0) {
		// Some succeeded, some failed
		return 'Your campaign was partially published';
	}

	// Nothing succeeded
	return 'Your campaign was not published';
};

/**
 * Builds the complete display information for all active channels
 */
export const buildChannelDisplayData = (
	publishedCreatives: IPublishedCreative[],
	failedCreatives: IFailedCreative[],
	channelLinks: ChannelLinksMap,
	channelInfoMap: { [key: string]: { name: string; icon: JSX.Element | null } },
	channelPriority: { [key: string]: number },
	comingSoonChannels: string[],
	creativePublishedErrorCode: string,
	selectedChannels: string[] = [],
): ChannelDisplayInfo[] => {
	// Get all channels that have activity (creatives)
	const activeChannels = getChannelsWithActivity(
		publishedCreatives,
		failedCreatives,
	);

	// Add selected coming soon channels to display
	const selectedComingSoonChannels = selectedChannels.filter((channelId) =>
		comingSoonChannels.includes(channelId),
	);

	// Combine active channels with selected coming soon channels
	const allChannelsToShow = Array.from(activeChannels);
	selectedComingSoonChannels.forEach((channelId) => {
		if (!allChannelsToShow.includes(channelId)) {
			allChannelsToShow.push(channelId);
		}
	});

	// Build display information for each channel to show
	return allChannelsToShow.map((channelId): ChannelDisplayInfo => {
		const channelInfo = channelInfoMap[channelId] || {
			name: channelId,
			icon: null,
		};

		const allErrors = getChannelErrors(channelId, failedCreatives);
		const successStatus = calculateChannelStatus(
			channelId,
			publishedCreatives,
			failedCreatives,
			creativePublishedErrorCode,
		);

		// Filter out CREATIVE_PUBLISHED errors for display (they're handled in status)
		const displayErrors = allErrors.filter(
			(error) => error.errorCode !== creativePublishedErrorCode,
		);

		return {
			channelId,
			channelName: channelInfo.name,
			channelIcon: channelInfo.icon,
			errors: displayErrors,
			successStatus,
			comingSoon: comingSoonChannels.includes(channelId),
			linkInfo: channelLinks[channelId],
			hasSpecificError: allErrors.some(
				(e) => e.errorCode === creativePublishedErrorCode,
			),
			errorCount: displayErrors.length,
			priority: channelPriority[channelId] || Infinity,
		};
	});
};

/**
 * Sorts channels by priority (errors first, then by type and priority)
 */
export const sortChannels = (
	channels: ChannelDisplayInfo[],
): ChannelDisplayInfo[] => {
	return channels.sort((a, b) => {
		const aHasWarnings = a.errorCount > 0;
		const bHasWarnings = b.errorCount > 0;

		// Channels with errors first
		if (aHasWarnings && !bHasWarnings) return -1;
		if (!aHasWarnings && bHasWarnings) return 1;

		// If both have errors, sort by error count (more errors first)
		if (aHasWarnings && bHasWarnings) return b.errorCount - a.errorCount;

		// Partial status channels next
		if (a.successStatus === 'partial' && b.successStatus !== 'partial')
			return -1;
		if (b.successStatus === 'partial' && a.successStatus !== 'partial')
			return 1;

		// Success status channels next
		if (a.successStatus === 'success' && b.successStatus !== 'success')
			return -1;
		if (b.successStatus === 'success' && a.successStatus !== 'success')
			return 1;

		// Coming soon channels last
		if (a.comingSoon && !b.comingSoon) return 1;
		if (!a.comingSoon && b.comingSoon) return -1;

		// Finally sort by channel priority
		return a.priority - b.priority;
	});
};
